<template>
  <el-dialog
    :model-value="modelValue"
    title="预设账号分组"
    width="920px"
    append-to-body
    class="group-manager-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <p class="intro">
      把<strong>不同或相同平台</strong>的多个账号放进同一预设组。纯小红书组进「小红书看板」（原创作中心汇总）；跨平台组进「分组看板」，也可再进小红书专页。
    </p>

    <div class="toolbar">
      <el-button type="primary" @click="startCreate">＋ 新建分组</el-button>
      <el-button :loading="loading" @click="reload">刷新</el-button>
    </div>

    <el-table :data="groups" v-loading="loading && !groups.length" size="small" stripe max-height="280">
      <el-table-column prop="name" label="分组名" min-width="140" />
      <el-table-column label="账号数" width="80" align="center">
        <template #default="{ row }">{{ row.count || 0 }}</template>
      </el-table-column>
      <el-table-column label="平台" min-width="160">
        <template #default="{ row }">
          {{ (row.platforms || []).map(platformName).join('、') || '—' }}
        </template>
      </el-table-column>
      <el-table-column label="成员预览" min-width="200" show-overflow-tooltip>
        <template #default="{ row }">
          {{ (row.accounts || []).map((a) => a.display_name).slice(0, 4).join('、') }}
          <span v-if="(row.accounts || []).length > 4">…</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="280" fixed="right">
        <template #default="{ row }">
          <el-button
            link
            type="success"
            :disabled="!boardMemberCount(row)"
            :title="boardMemberCount(row) ? '打开分组看板' : '成员账号已失效（易媒同步后 ID 变更），请编辑分组重新勾选'"
            @click="openGroupBoard(row)"
          >{{ isXhsOnlyGroup(row) ? '小红书看板' : '分组看板' }}</el-button>
          <el-button
            v-if="xhsMemberCount(row) && !isXhsOnlyGroup(row)"
            link
            type="warning"
            @click="openXhsGroupBoard(row)"
          >小红书看板</el-button>
          <el-button link type="primary" @click="startEdit(row)">编辑</el-button>
          <el-button link type="danger" @click="removeGroup(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-divider v-if="editing" />

    <div v-if="editing" class="editor">
      <div class="editor-title">{{ form.id ? '编辑分组' : '新建分组' }}</div>
      <el-form label-width="80px" size="default">
        <el-form-item label="分组名" required>
          <el-input v-model="form.name" placeholder="如：主号矩阵 / 同人线" maxlength="40" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="form.description" placeholder="可选" maxlength="120" />
        </el-form-item>
        <el-form-item label="成员账号">
          <div class="member-picker">
            <div class="picker-side">
              <el-select v-model="pickerPlatform" clearable placeholder="平台" size="small" style="width: 100%">
                <el-option
                  v-for="p in platformOptions"
                  :key="p.code"
                  :label="p.name"
                  :value="p.code"
                />
              </el-select>
              <el-input
                v-model="pickerKeyword"
                placeholder="搜索账号"
                clearable
                size="small"
                style="margin-top: 8px"
              />
              <el-checkbox
                :model-value="pickerAllChecked"
                :indeterminate="pickerIndeterminate"
                style="margin-top: 8px"
                @change="togglePickerAll"
              >全选当前列表</el-checkbox>
            </div>
            <div class="picker-grid">
              <div
                v-for="acc in filteredAccounts"
                :key="accountKey(acc)"
                class="picker-card"
                :class="{ selected: isSelected(acc) }"
                @click="toggleMember(acc)"
              >
                <el-checkbox :model-value="isSelected(acc)" @click.stop @change="() => toggleMember(acc)" />
                <div class="picker-meta">
                  <strong>{{ acc.display_name || acc.account_id }}</strong>
                  <small>{{ platformName(acc.platform) }}</small>
                </div>
              </div>
              <el-empty v-if="!filteredAccounts.length" description="无匹配账号" :image-size="40" />
            </div>
          </div>
          <div class="selected-tip">
            已选 {{ form.members.length }} 个账号（可跨平台）
            <el-button
              v-if="form.members.length"
              link
              type="warning"
              size="small"
              @click="clearMembers"
            >清空成员</el-button>
          </div>
        </el-form-item>
      </el-form>
      <div class="editor-actions">
        <el-button @click="editing = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存分组</el-button>
      </div>
    </div>

    <template #footer>
      <el-button @click="emit('update:modelValue', false)">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { publishV2Api } from '../api'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  accounts: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'changed'])
const router = useRouter()

const boardMemberCount = (row) =>
  (row?.accounts || []).filter(
    (a) => ['xiaohongshu', 'zhihu', 'douyin', 'toutiao', 'baijiahao'].includes(a.platform) && a.exists !== false,
  ).length

const xhsMemberCount = (row) =>
  (row?.accounts || []).filter(
    (a) => a.platform === 'xiaohongshu' && a.exists !== false,
  ).length

const isXhsOnlyGroup = (row) => {
  const board = (row?.accounts || []).filter(
    (a) => ['xiaohongshu', 'zhihu', 'douyin', 'toutiao', 'baijiahao'].includes(a.platform) && a.exists !== false,
  )
  return board.length > 0 && board.every((a) => a.platform === 'xiaohongshu')
}

const openXhsGroupBoard = (row) => {
  if (!row?.id || !xhsMemberCount(row)) {
    ElMessage.warning('该分组没有可用的小红书账号')
    return
  }
  emit('update:modelValue', false)
  router.push({ path: `/data-analytics/xhs/group/${row.id}`, query: { range: '7' } })
}

const openGroupBoard = (row) => {
  if (!row?.id || !boardMemberCount(row)) {
    ElMessage.warning('该分组没有可用的小红书/知乎/抖音/头条/百家号账号')
    return
  }
  emit('update:modelValue', false)
  // 纯小红书组：进原来的小红书分组看板（诊断/趋势/笔记完整页）
  if (isXhsOnlyGroup(row)) {
    router.push({ path: `/data-analytics/xhs/group/${row.id}`, query: { range: '7' } })
    return
  }
  router.push({ path: `/data-analytics/group/${row.id}`, query: { range: '7' } })
}

const loading = ref(false)
const saving = ref(false)
const groups = ref([])
const editing = ref(false)
const pickerPlatform = ref('')
const pickerKeyword = ref('')

const form = reactive({
  id: null,
  name: '',
  description: '',
  members: [],
})

const platformOptions = [
  { code: 'xiaohongshu', name: '小红书' },
  { code: 'douyin', name: '抖音' },
  { code: 'zhihu', name: '知乎' },
  { code: 'toutiao', name: '头条' },
  { code: 'baijiahao', name: '百家号' },
  { code: 'bilibili', name: 'B站' },
  { code: 'kuaishou', name: '快手' },
  { code: 'weibo', name: '微博' },
]

const platformName = (code) =>
  platformOptions.find((p) => p.code === code)?.name || code || '—'

const accountKey = (acc) => `${acc.platform}:${acc.account_id}`

const filteredAccounts = computed(() => {
  let list = props.accounts || []
  if (pickerPlatform.value) {
    list = list.filter((a) => a.platform === pickerPlatform.value)
  }
  const kw = pickerKeyword.value.trim().toLowerCase()
  if (kw) {
    list = list.filter(
      (a) =>
        (a.display_name || '').toLowerCase().includes(kw)
        || (a.account_id || '').toLowerCase().includes(kw),
    )
  }
  return list
})

const selectedSet = computed(() => new Set(form.members.map(accountKey)))

const isSelected = (acc) => selectedSet.value.has(accountKey(acc))

const pickerAllChecked = computed(() => {
  const list = filteredAccounts.value
  if (!list.length) return false
  return list.every((a) => selectedSet.value.has(accountKey(a)))
})

const pickerIndeterminate = computed(() => {
  const list = filteredAccounts.value
  const n = list.filter((a) => selectedSet.value.has(accountKey(a))).length
  return n > 0 && n < list.length
})

const toggleMember = (acc) => {
  const k = accountKey(acc)
  const idx = form.members.findIndex((m) => accountKey(m) === k)
  if (idx >= 0) {
    form.members.splice(idx, 1)
  } else {
    form.members.push({ platform: acc.platform, account_id: acc.account_id })
  }
}

const togglePickerAll = (checked) => {
  const list = filteredAccounts.value
  if (checked) {
    const map = new Map(form.members.map((m) => [accountKey(m), m]))
    for (const a of list) {
      map.set(accountKey(a), { platform: a.platform, account_id: a.account_id })
    }
    form.members = [...map.values()]
  } else {
    const remove = new Set(list.map(accountKey))
    form.members = form.members.filter((m) => !remove.has(accountKey(m)))
  }
}

const reload = async ({ quiet = false } = {}) => {
  if (!quiet) loading.value = true
  try {
    const res = await publishV2Api.listAccountGroups()
    const data = res?.data || res
    groups.value = data.items || []
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || e?.message || '加载分组失败')
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  form.id = null
  form.name = ''
  form.description = ''
  form.members = []
  pickerPlatform.value = ''
  pickerKeyword.value = ''
}

const startCreate = () => {
  resetForm()
  editing.value = true
}

const startEdit = (row) => {
  form.id = row.id
  form.name = row.name || ''
  form.description = row.description || ''
  form.members = (row.members || row.accounts || []).map((m) => ({
    platform: m.platform,
    account_id: m.account_id,
  }))
  editing.value = true
}

const clearMembers = () => {
  form.members = []
}

const save = async () => {
  if (!form.name.trim()) {
    ElMessage.warning('请填写分组名')
    return
  }
  if (!form.id && !form.members.length) {
    ElMessage.warning('新建分组请至少选择一个账号')
    return
  }
  saving.value = true
  try {
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      members: form.members,
    }
    if (form.id) {
      await publishV2Api.updateAccountGroup(form.id, payload)
      ElMessage.success('分组已更新')
    } else {
      await publishV2Api.createAccountGroup(payload)
      ElMessage.success('分组已创建')
    }
    editing.value = false
    await reload()
    emit('changed')
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

const removeGroup = async (row) => {
  try {
    await ElMessageBox.confirm(`确定删除分组「${row.name}」？不影响账号本身。`, '删除分组', {
      type: 'warning',
    })
  } catch {
    return
  }
  try {
    await publishV2Api.deleteAccountGroup(row.id)
    ElMessage.success('已删除')
    if (form.id === row.id) editing.value = false
    await reload()
    emit('changed')
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || e?.message || '删除失败')
  }
}

watch(
  () => props.modelValue,
  (v) => {
    if (v) {
      editing.value = false
      // 已有缓存时后台刷新，避免整表遮罩转圈
      reload({ quiet: groups.value.length > 0 })
    }
  },
)

defineExpose({ reload })
</script>

<style scoped>
.intro {
  margin: 0 0 12px;
  font-size: 13px;
  color: #606266;
}

.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.editor {
  background: #f7f9fc;
  border-radius: 8px;
  padding: 14px 16px;
}

.editor-title {
  font-weight: 700;
  margin-bottom: 12px;
}

.member-picker {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 12px;
  width: 100%;
}

.picker-side {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 10px;
}

.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
  max-height: 260px;
  overflow: auto;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 10px;
}

.picker-card {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
}

.picker-card.selected {
  border-color: #7aa2ff;
  background: #f3f7ff;
}

.picker-meta strong {
  display: block;
  font-size: 12px;
}

.picker-meta small {
  color: #909399;
  font-size: 11px;
}

.selected-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
</style>
